import { useCallback, useEffect, useMemo, useState } from "react";
import {
  getHubMarketplaceEntries,
  getAppCatalogEntry,
} from "../../app-catalog/index.js";
import { normalizeGovernanceAppKey } from "@tennis-matches/services/governance";
import {
  areFeatureFlagsReadySync,
  isFeatureEnabledSync,
  loadFeatureFlags,
} from "@tennis-matches/services/feature-flags";
import {
  PLATFORM_APP_USAGE_KEY,
  PLATFORM_PINNED_APPS_KEY,
  PLATFORM_RECENT_APPS_KEY,
  PLATFORM_RECENT_APPS_LIMIT,
} from "./constants.js";

function canUseStorage() {
  return (
    typeof window !== "undefined" && typeof window.localStorage !== "undefined"
  );
}

function readJson(key, fallback) {
  if (!canUseStorage()) return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return parsed ?? fallback;
  } catch {
    return fallback;
  }
}

function writeJson(key, value) {
  if (!canUseStorage()) return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // ignore storage errors (quota/private mode)
  }
}

function appKeyOf(appOrKey) {
  if (!appOrKey) return "";
  if (typeof appOrKey === "string")
    return normalizeGovernanceAppKey(appOrKey.trim());
  return normalizeGovernanceAppKey(
    String(appOrKey?.appKey || appOrKey?.id || "").trim(),
  );
}

function appLabel(app) {
  return String(app?.title || app?.appKey || app?.id || "").trim();
}

/**
 * Normalisiert effektive Launcher-/Hub-Keys zu einem Set (oder null = noch nicht aufgelöst).
 * @param {Set<string>|string[]|null|undefined} allowedAppKeys
 * @returns {Set<string>|null}
 */
export function resolveLauncherAllowedAppKeysSet(allowedAppKeys) {
  if (allowedAppKeys == null) return null;
  if (allowedAppKeys instanceof Set) {
    const out = new Set();
    for (const x of allowedAppKeys) {
      const k = normalizeGovernanceAppKey(x);
      if (k) out.add(k);
    }
    return out;
  }
  if (Array.isArray(allowedAppKeys)) {
    const out = new Set();
    for (const x of allowedAppKeys) {
      const k = normalizeGovernanceAppKey(x);
      if (k) out.add(k);
    }
    return out;
  }
  return new Set();
}

function featureFlagKeyForApp(appOrKey) {
  const key = appKeyOf(appOrKey);
  if (!key) return "";
  const entry = getAppCatalogEntry(key);
  return normalizeGovernanceAppKey(entry?.featureFlagKey || key);
}

/**
 * Launcher-/Hub-Sichtbarkeit: Feature-Flags + effektive Session-Apps
 * (Manifest global∧tenant ∩ Rollen-Matrix, siehe effectiveVisibleAppKeys).
 * - `allowedAppKeys` null/undefined: noch nicht aufgelöst → nichts anzeigen.
 * - `allowedAppKeys` (auch leer): nur Keys aus dem Set (auch für Superadmin).
 */
export function isAppVisibleInLauncher(
  appOrKey,
  { allowedAppKeys = null } = {},
) {
  const key = appKeyOf(appOrKey);
  if (!key) return false;
  if (isFeatureEnabledSync(featureFlagKeyForApp(key)) === false) return false;

  const allowed = resolveLauncherAllowedAppKeysSet(allowedAppKeys);
  if (allowed == null) return false;

  return allowed.has(key);
}

export function useAppLauncherState(options = {}) {
  /** Solange false: kein Katalog rendern (Governance/Manifeste noch nicht konsistent mit Route-Guard). */
  const accessReady = options?.accessReady !== false;
  const canAccessApp =
    typeof options?.canAccessApp === "function" ? options.canAccessApp : null;
  const [flagsTick, setFlagsTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    if (areFeatureFlagsReadySync())
      return () => {
        cancelled = true;
      };
    loadFeatureFlags()
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setFlagsTick((x) => x + 1);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onRefresh = () => setFlagsTick((x) => x + 1);
    window.addEventListener("tm:governance-bundle-refresh", onRefresh);
    return () =>
      window.removeEventListener("tm:governance-bundle-refresh", onRefresh);
  }, []);
  void flagsTick;

  const allowedAppKeysSet = useMemo(
    () => resolveLauncherAllowedAppKeysSet(options?.allowedAppKeys),
    [options?.allowedAppKeys],
  );

  const appEntries = useMemo(() => {
    if (!accessReady) return [];
    const list = getHubMarketplaceEntries();
    const safe = Array.isArray(list) ? list : [];
    // Gleiche Quelle/Filter wie GlobalHubPage (getHubMarketplaceEntries + Feature-Flags).
    return safe.filter((app) => {
      if (
        !isAppVisibleInLauncher(app, {
          allowedAppKeys: allowedAppKeysSet,
        })
      )
        return false;
      if (canAccessApp && canAccessApp(app) === false) return false;
      return true;
    });
  }, [accessReady, allowedAppKeysSet, canAccessApp, flagsTick]);

  const appByKey = useMemo(() => {
    const map = new Map();
    appEntries.forEach((app) => {
      const key = appKeyOf(app);
      if (!key) return;
      map.set(key, app);
    });
    return map;
  }, [appEntries]);

  const [recentRaw, setRecentRaw] = useState(() => {
    const rows = readJson(PLATFORM_RECENT_APPS_KEY, []);
    if (!Array.isArray(rows)) return [];
    return rows
      .map((row) => {
        const key = appKeyOf(row?.appKey || row?.id || "");
        if (!key) return null;
        return {
          appKey: key,
          timestamp: Number(row?.timestamp || 0) || 0,
          count: Number(row?.count || 0) || 0,
        };
      })
      .filter(Boolean);
  });

  const [pinnedRaw, setPinnedRaw] = useState(() => {
    const rows = readJson(PLATFORM_PINNED_APPS_KEY, []);
    if (!Array.isArray(rows)) return [];
    return rows.map((x) => appKeyOf(x)).filter(Boolean);
  });

  const [usageRaw, setUsageRaw] = useState(() => {
    const obj = readJson(PLATFORM_APP_USAGE_KEY, {});
    if (!obj || typeof obj !== "object") return {};
    const safe = {};
    Object.entries(obj).forEach(([k, v]) => {
      const key = appKeyOf(k);
      if (!key) return;
      safe[key] = Number(v || 0) || 0;
    });
    return safe;
  });

  const __shouldDropFromHistory = useCallback(
    (key) =>
      !isAppVisibleInLauncher(key, {
        allowedAppKeys: allowedAppKeysSet,
      }),
    [allowedAppKeysSet],
  );

  useEffect(() => {
    if (!allowedAppKeysSet || allowedAppKeysSet.size === 0) return;

    setRecentRaw((prev) => {
      const cur = Array.isArray(prev) ? prev : [];
      const next = cur.filter((r) => !__shouldDropFromHistory(r.appKey));
      if (next.length === cur.length) return prev;
      writeJson(PLATFORM_RECENT_APPS_KEY, next);
      return next;
    });

    setPinnedRaw((prev) => {
      const cur = Array.isArray(prev) ? prev : [];
      const next = cur.filter((k) => !__shouldDropFromHistory(k));
      if (next.length === cur.length) return prev;
      writeJson(PLATFORM_PINNED_APPS_KEY, next);
      return next;
    });

    setUsageRaw((prev) => {
      const o = prev && typeof prev === "object" ? prev : {};
      const next = { ...o };
      let changed = false;
      for (const k of Object.keys(next)) {
        if (__shouldDropFromHistory(k)) {
          delete next[k];
          changed = true;
        }
      }
      if (changed) writeJson(PLATFORM_APP_USAGE_KEY, next);
      return changed ? next : prev;
    });
  }, [allowedAppKeysSet, __shouldDropFromHistory]);

  const isPinned = useCallback(
    (appOrKey) => {
      const key = appKeyOf(appOrKey);
      if (!key) return false;
      return pinnedRaw.includes(key);
    },
    [pinnedRaw],
  );

  const getUsageScore = useCallback(
    (appOrKey) => {
      const key = appKeyOf(appOrKey);
      if (!key) return 0;
      const usageCount = Number(usageRaw[key] || 0) || 0;
      const recentIndex = recentRaw.findIndex((x) => x.appKey === key);
      const recentBonus =
        recentIndex >= 0
          ? Math.max(0, PLATFORM_RECENT_APPS_LIMIT - recentIndex)
          : 0;
      return usageCount * 10 + recentBonus;
    },
    [recentRaw, usageRaw],
  );

  const openApp = useCallback(
    (appOrKey) => {
      const key = appKeyOf(appOrKey);
      if (!key) return;
      if (
        !isAppVisibleInLauncher(key, {
          allowedAppKeys: allowedAppKeysSet,
        })
      )
        return;

      setUsageRaw((prev) => {
        const next = { ...(prev || {}) };
        next[key] = (Number(next[key] || 0) || 0) + 1;
        writeJson(PLATFORM_APP_USAGE_KEY, next);
        return next;
      });

      setRecentRaw((prev) => {
        const now = Date.now();
        const current = Array.isArray(prev) ? prev : [];
        const existing = current.find((r) => r.appKey === key) || null;
        const nextRow = {
          appKey: key,
          timestamp: now,
          count: (Number(existing?.count || 0) || 0) + 1,
        };
        const next = [
          nextRow,
          ...current.filter((r) => r.appKey !== key),
        ].slice(0, PLATFORM_RECENT_APPS_LIMIT);
        writeJson(PLATFORM_RECENT_APPS_KEY, next);
        return next;
      });
    },
    [allowedAppKeysSet],
  );

  const pinApp = useCallback((appOrKey) => {
    const key = appKeyOf(appOrKey);
    if (!key) return;
    setPinnedRaw((prev) => {
      const current = Array.isArray(prev) ? prev : [];
      if (current.includes(key)) return current;
      const next = [key, ...current];
      writeJson(PLATFORM_PINNED_APPS_KEY, next);
      return next;
    });
  }, []);

  const unpinApp = useCallback((appOrKey) => {
    const key = appKeyOf(appOrKey);
    if (!key) return;
    setPinnedRaw((prev) => {
      const current = Array.isArray(prev) ? prev : [];
      const next = current.filter((x) => x !== key);
      writeJson(PLATFORM_PINNED_APPS_KEY, next);
      return next;
    });
  }, []);

  const allApps = useMemo(() => appEntries, [appEntries]);

  const recentApps = useMemo(() => {
    return recentRaw.map((r) => appByKey.get(r.appKey)).filter(Boolean);
  }, [recentRaw, appByKey]);

  const pinnedApps = useMemo(() => {
    return pinnedRaw.map((k) => appByKey.get(k)).filter(Boolean);
  }, [pinnedRaw, appByKey]);

  const launcherApps = useMemo(() => {
    const list = [...allApps];
    list.sort((a, b) => {
      const aKey = appKeyOf(a);
      const bKey = appKeyOf(b);
      const aPinned = isPinned(aKey) ? 1 : 0;
      const bPinned = isPinned(bKey) ? 1 : 0;
      if (aPinned !== bPinned) return bPinned - aPinned;

      const aScore = getUsageScore(aKey);
      const bScore = getUsageScore(bKey);
      if (aScore !== bScore) return bScore - aScore;

      return appLabel(a).localeCompare(appLabel(b), "de");
    });
    return list;
  }, [allApps, getUsageScore, isPinned]);

  return {
    recentApps,
    pinnedApps,
    allApps,
    launcherApps,
    openApp,
    pinApp,
    unpinApp,
    isPinned,
    getUsageScore,
  };
}
